import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { FILE_LANE, UploadEngine } from '@lite-workspace/uploader-core';
import { PSD_RENDER_RULE_VERSION } from '@lite-workspace/dto-fotoclass-templates';
import { designSpreadPreviewFileId, designSpreadPreviewFileName } from './design-spread-preview-id';

/** The three rendered tiers a pack spread keeps as its previewVersions. */
export interface DesignSpreadPreviewTiers {
  format: 'webp' | 'jpeg';
  width: number;
  height: number;
  mediumBlob: Blob;
  thumbBlob: Blob;
  largeBlob: Blob;
  thumbWidth: number;
  thumbHeight: number;
  largeWidth: number;
  largeHeight: number;
  /** Empty when the capture skipped it: the commit then carries none. */
  thumbHash?: string;
  fontDegraded?: boolean;
}

/** Commits an editor-rendered preview onto a pack spread — the cabinet and the album (vignette versions) alike. */
@Injectable({ providedIn: 'root' })
export class DesignSpreadPreviewUpload {
  private readonly http = inject(HttpClient);
  private readonly engine = inject(UploadEngine);

  // Uploads large/thumb/medium under deterministic ids (same spread → same storage object, overwritten in place)
  // and commits them as the spread's durable previewVersions.
  async upload(designPackageId: string, spreadId: string, preview: DesignSpreadPreviewTiers): Promise<void> {
    const ext = preview.format === 'webp' ? 'webp' : 'jpg';
    const mediumFileId = designSpreadPreviewFileId(designPackageId, spreadId, 'medium');
    const thumbFileId = designSpreadPreviewFileId(designPackageId, spreadId, 'thumb');
    const largeFileId = designSpreadPreviewFileId(designPackageId, spreadId, 'large');
    const asFile = (blob: Blob, fileId: string): File =>
      new File([blob], designSpreadPreviewFileName(fileId, ext), { type: blob.type });
    const [mediumPath, thumbPath, largePath] = await Promise.all([
      this.uploadFile(designPackageId, asFile(preview.mediumBlob, mediumFileId), mediumFileId),
      this.uploadFile(designPackageId, asFile(preview.thumbBlob, thumbFileId), thumbFileId),
      this.uploadFile(designPackageId, asFile(preview.largeBlob, largeFileId), largeFileId),
    ]);
    await firstValueFrom(
      this.http.post<void>(`/api/design-packages/${designPackageId}/spreads/previews`, {
        spreads: [
          {
            id: spreadId,
            format: preview.format,
            medium: { fileId: mediumFileId, path: mediumPath, width: preview.width, height: preview.height },
            thumb: { fileId: thumbFileId, path: thumbPath, width: preview.thumbWidth, height: preview.thumbHeight },
            large: { fileId: largeFileId, path: largePath, width: preview.largeWidth, height: preview.largeHeight },
            thumbHash: preview.thumbHash || undefined,
            fontsDegraded: preview.fontDegraded === true,
            // Правило рендера ЭТОГО бандла: пиксели нарисовала вкладка, сервер свою константу поверх
            // не ставит — иначе вкладка, открытая до выката, записала бы старое как свежее.
            renderRuleVersion: PSD_RENDER_RULE_VERSION,
          },
        ],
      }),
    );
  }

  // Routes through the shared upload engine (retry included) instead of a bespoke HttpClient call.
  private async uploadFile(designPackageId: string, file: File, fileId: string): Promise<string> {
    const uploaded = await this.engine.uploadAndWait({
      lane: FILE_LANE,
      key: designPackageId,
      file,
      meta: { projectId: designPackageId, fileId, uploadProcess: 'asset' },
    });
    if (!uploaded?.pathToFileInStorage) throw new Error('preview upload returned no storage path');
    return uploaded.pathToFileInStorage;
  }
}
